/**
 * Fetch a patient's health-insurance list.
 *
 * Wire (Flutter `patient_data_source_impl.dart:285–293`):
 *   GET `/api/Patients/HealthInsuranceList/<patientId>` → `HealthInsurance[]`
 *
 * The companion save hook lives at `use-save-health-insurance.ts` and
 * was wire-verified against a dev curl on 2026-05-11 (PROD-715). Keep
 * this fetch hook simple — it just reads. The save side owns the
 * mutation cleanup and the listed wire quirks.
 */

import { useQuery } from '@tanstack/react-query';
import { get, ApiError } from '@/shared/api/api-client';
import type { HealthInsurance } from '@/services/scheduling/use-prebook-selected-patient';

export function useHealthInsuranceList(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['health-insurance', patientId],
    queryFn: async ({ signal }) => {
      // 404 = "this patient has no health insurance" — a valid EMPTY state,
      // NOT an error. The backend signals absence with a 404 (rather than
      // 200 + []), so translate it to an empty list here. Otherwise the
      // shared client throws ApiError, the query goes isError + retries 3×,
      // and the global onError surfaces a red toast on every no-insurance
      // patient. Mirrors the 404→empty convention in shared/services/lookups.ts
      // and use-encounter-by-appointment.ts.
      try {
        return await get<HealthInsurance[]>(
          `/api/Patients/HealthInsuranceList/${patientId}`,
          { signal },
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: !!patientId,
  });
}
